import { useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuthedGet, useAuthedHttp, errMsg } from "@/lib/api";
import { Field } from "./Fields";

const defaults = (schema) => Object.fromEntries(schema.map((f) => [f.name, f.default ?? (["list", "images", "multi"].includes(f.type) ? [] : f.type === "bool" ? true : f.type === "number" ? null : "")]));

export function ResourceManager({ res, title, schema, columns }) {
  const call = useAuthedHttp();
  const qc = useQueryClient();
  const { data = [], isLoading } = useAuthedGet(`/admin/res/${res}`);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["authed", `/admin/res/${res}`] }); qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === "string" }); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { id, ...body } = edit;
      const visible = Object.fromEntries(schema.map((f) => [f.name, body[f.name]]));
      if (id) await call("put", `/admin/res/${res}/${id}`, { ...body, ...visible });
      else await call("post", `/admin/res/${res}`, visible);
      toast.success("Saved");
      setEdit(null);
      refresh();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  const del = async (row) => {
    if (!window.confirm(`Delete "${row.title || row.name}"?`)) return;
    try { await call("delete", `/admin/res/${res}/${row.id}`); toast.success("Deleted"); refresh(); } catch (er) { toast.error(errMsg(er)); }
  };

  return (
    <div data-testid={`admin-${res}`}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-4xl">{title}</h1>
        <button onClick={() => setEdit(defaults(schema))} className="btn-neon py-3" data-testid={`admin-${res}-add-button`}><Plus className="w-4 h-4" />Add</button>
      </div>
      {isLoading ? <Loader2 className="animate-spin" /> : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#171717] text-left font-mono text-[11px] uppercase tracking-wider text-grey"><tr>{columns.map((c) => <th key={c.label} className="p-3">{c.label}</th>)}<th className="p-3 w-24" /></tr></thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.id} className="border-t border-white/10 hover:bg-white/[0.02]" data-testid={`admin-${res}-row-${row.slug || row.id}`}>
                  {columns.map((c) => <td key={c.label} className="p-3 align-middle">{c.render(row)}</td>)}
                  <td className="p-3 whitespace-nowrap">
                    <button onClick={() => setEdit(row)} className="p-2 hover:text-neon" data-testid={`admin-${res}-edit-${row.slug || row.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(row)} className="p-2 hover:text-red-400" data-testid={`admin-${res}-delete-${row.slug || row.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {!data.length && <tr><td colSpan={columns.length + 1} className="p-8 text-center text-grey">Nothing here yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#111] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`admin-${res}-dialog`}>
          <DialogTitle className="font-display text-3xl">{edit?.id ? "EDIT" : "ADD"} {title.replace(/S$/, "")}</DialogTitle>
          {edit && (
            <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 mt-2">
              {schema.map((f) => (
                <div key={f.name} className={f.wide || ["textarea", "images", "image", "video", "multi"].includes(f.type) ? "sm:col-span-2" : ""}>
                  <span className="label">{f.label}{f.required && " *"}</span>
                  <Field f={f} value={edit[f.name]} onChange={(v) => setEdit((s) => ({ ...s, [f.name]: v }))} />
                </div>
              ))}
              <button disabled={busy} className="btn-neon sm:col-span-2 mt-2" data-testid={`admin-${res}-save-button`}>{busy && <Loader2 className="w-4 h-4 animate-spin" />}Save</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
